'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, apiFetch } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

export type ConsumptionAnomalyKind = 'picco' | 'zero_prolungato' | 'crollo';

export interface ConsumptionAnomalyItem {
	id: string;
	meterId: string;
	/** Codice POD, risolto dall'API; `null` se il misuratore non ce l'ha. */
	codice: string | null;
	/**
	 * Il punto di connessione del misuratore, per scendere all'esploso per POD.
	 * `null` finché il codice POD non è stato inserito — l'aggiunta rapida del
	 * socio crea il misuratore col solo nome.
	 */
	podId: string | null;
	kind: ConsumptionAnomalyKind;
	severity: 'critical' | 'warning';
	/** Giorno del consumo anomalo (il primo, per le anomalie che durano). */
	occurredOn: string;
	lastDay: string;
	days: number;
	value: number | null;
	baseline: number | null;
	detectedAt: string | null;
	resolvedAt: string | null;
	resolution: 'auto' | 'manual' | null;
	resolvedBy: string | null;
}

export type AnomalyStatusFilter = 'open' | 'resolved' | 'all';

const EMPTY: ConsumptionAnomalyItem[] = [];

/**
 * Storico delle irregolarità nei consumi (`GET /api/anomalies`).
 *
 * Nessuna sottoscrizione realtime, al contrario degli altri elenchi di questa
 * app: le righe le scrive il cron una volta al giorno, quindi un canale aperto
 * su `consumption_anomalies` resterebbe muto per ventiquattro ore e poi
 * produrrebbe un solo evento — che l'amministratore vede comunque al prossimo
 * caricamento della pagina, oltre che come notifica.
 *
 * ADR-006: backed by React Query.
 */
export function useConsumptionAnomalies(
	orgId: string | null | undefined,
	status: AnomalyStatusFilter = 'all',
) {
	const loaded = useAuthStore((s) => s.loaded);
	const enabled = loaded && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: ['consumption-anomalies', orgId, status],
		enabled,
		queryFn: () =>
			api.get<ConsumptionAnomalyItem[]>('/api/anomalies', { orgId: orgId as string, status }),
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	/** Chiude o riapre un'anomalia; torna true se il salvataggio è andato a buon fine. */
	const setResolved = useCallback(
		async (id: string, resolved: boolean): Promise<boolean> => {
			if (!orgId) return false;
			const res = await apiFetch(`/api/anomalies/${id}?orgId=${orgId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ resolved }),
			});
			if (!res.ok) return false;
			await rqRefetch();
			return true;
		},
		[orgId, rqRefetch],
	);

	return {
		anomalies: Array.isArray(data) ? data : EMPTY,
		isLoading: enabled ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento delle anomalie'
			: null,
		refetch,
		setResolved,
	};
}
